import { cn } from "cy-fe/src/utils/cn";
import { useId } from "react";

const allowedKeys = [
  "Backspace",
  "Delete",
  "ArrowLeft",
  "ArrowRight",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "0",
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
  "Tab",
];
const letters = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];
export function OtpInput({
  length,
  className,
  onChange,
  value,
  placeholder = "",
  numberOnly = false,
  "data-testid": dataTestId,
}: {
  numberOnly?: boolean;
  className?: string;
  onChange: (value: string) => void;
  value: string;
  length: number;
  placeholder?: string;
  "data-testid"?: string;
}) {
  const inputs = new Array(length).fill(" ");
  value = value || inputs.join("");
  const id = useId();
  function triggerChange(newValue: string) {
    onChange(newValue.toUpperCase().substring(0, length));
  }
  function handleChange(inputChar: string, index: number) {
    const newValue = [...value];
    newValue[index] = inputChar || " ";
    triggerChange(newValue.join(""));
    document.getElementById(`${id}-${index + 1}`)?.focus();
  }

  function onKeyUp(e: any, index: number) {
    if (e.key === e.target.value) {
      document.getElementById(`${id}-${index + 1}`)?.focus();
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>, index: number) {
    const isAllowedSpecialKey =
      (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "v";
    // Allow thru
    if (
      !isAllowedSpecialKey &&
      !allowedKeys.includes(e.key) &&
      !allowedKeys.includes(e.key.toUpperCase())
    ) {
      e.preventDefault();
      return;
    }

    if (
      !isAllowedSpecialKey &&
      numberOnly &&
      letters.includes(e.key.toUpperCase())
    ) {
      e.preventDefault();
      return;
    }
    if (e.key === "Backspace") {
      e.preventDefault();
      const newValue = [...value];
      newValue[index] = " ";
      triggerChange(newValue.join(""));
      document.getElementById(`${id}-${index - 1}`)?.focus();
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      document.getElementById(`${id}-${index - 1}`)?.focus();
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      document.getElementById(`${id}-${index + 1}`)?.focus();
    }
  }
  function onPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pastedValue = e.clipboardData.getData("text");
    const newValue = [...value];
    pastedValue.split("").forEach((char, index) => {
      newValue[index] = char;
    });
    triggerChange(newValue.join(""));
    document.getElementById(`${id}-${pastedValue.length}`)?.focus();
  }
  return (
    <div
      data-testid={dataTestId}
      className={cn("flex w-full items-center justify-around gap-2", className)}
    >
      {inputs.map((_, index) => (
        <input
          onPaste={onPaste}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          key={index}
          className={cn(
            "block h-10 w-10 grow-0 rounded px-1 py-1 text-center text-2xl",
            "sm:h-14 sm:w-14 sm:text-3xl",
            "border-control-border border",
            "ring-[length:var(--control-ring-width-base)] outline-none",
            "hover:border-control-border-hover",
            "hover:ring-[length:var(--control-ring-width-hover)]",
            "hover:ring-control-ring-hover",
            "focus:border-control-border-focus",
            "focus:ring-[length:var(--control-ring-width-focus)]",
            "focus:ring-control-ring-focus",
            className,
          )}
          onChange={(e) => handleChange(e.target.value, index)}
          value={value[index].toUpperCase().trim() || ""}
          maxLength={1}
          placeholder={placeholder}
          onFocus={(e) => e.target.select()}
          type="text"
          onKeyUp={(e) => onKeyUp(e, index)}
          onKeyDown={(e) => onKeyDown(e, index)}
          id={`${id}-${index}`}
          data-testid={`${dataTestId || "otp-input"}-${index}`}
        />
      ))}
    </div>
  );
}
